import React, { useState, useEffect } from 'react';
import { Container } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getGcmMerchants } from '@/services/program/getGcmMerchants';
import { verifyPfiUser } from '@/services/program/getMerchants';
import { STATIC_CATEGORIES, CategoryTab, categoryPath } from '@/containers/Participant/GcmShared/categories';
import { YelpRating } from '@/containers/Participant/GcmShared/yelp';
import { GcmSearchBar } from '@/containers/Participant/GcmShared/GcmHeader';
import MerchantPhoto from '@/containers/Participant/GcmShared/MerchantPhoto';
import { searchPath, openBusiness, yelpBusinessUrl } from '@/containers/Participant/GcmShared/helpers';
import useGcmLocation from '@/containers/Participant/GcmShared/useGcmLocation';
import OurMerchants from '@/containers/Participant/RedeemPoints/components/BrowseMerchant';
import './index.scss';

const LOCAL_SPOTS_SHOWN = 7;

const BrowseMerchantsNew = ({ program }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const geo = useGcmLocation();
    const { location } = geo;
    const [searchInput, setSearchInput] = useState('');
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const showPfiBanner = program?.pfi?.enable_pfi && program?.pfi_merchant_id;

    useEffect(() => {
        if (!location) return undefined;
        let cancelled = false;
        setLoading(true);
        getGcmMerchants({ location })
            .then(data => { if (!cancelled) setBusinesses(data.businesses || []); })
            .catch(console.error)
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [location]);

    // Searches open the results page; an empty search leaves this page as is
    const handleApply = () => {
        const query = searchInput.trim();
        if (query) navigate(searchPath(query, location));
    };

    const handleBannerClick = async () => {
        if (pageLoading) return;
        setPageLoading(true);
        try {
            const result = await verifyPfiUser(program.id, program.pfi_merchant_id);
            if (result.status !== '200') {
                alert(result.message || 'User verification required');
                return;
            }
            if (result?.url) window.location.href = result.url;
        } catch (error) {
            console.error(error);
            alert('Verification failed. Please try again.');
        } finally {
            setTimeout(() => setPageLoading(false), 500);
        }
    };

    return (
        <div className="bmn-page">
            {pageLoading && (
                <div className="full-page-loader">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="mt-3">Please wait…</p>
                </div>
            )}
            <Container>
                <GcmSearchBar
                    value={searchInput}
                    onChange={setSearchInput}
                    onApply={handleApply}
                    onReset={() => setSearchInput('')}
                    geo={geo}
                    brand
                />

                {/* Local Merchants */}
                <section className="bmn-section">
                    <h2 className="bmn-section__title">Local Merchants</h2>
                    <div className="bmn-categories">
                        {STATIC_CATEGORIES.map(cat => (
                            <CategoryTab
                                key={cat.id}
                                category={cat}
                                className="bmn-category"
                                iconSize={64}
                                onClick={() => navigate(categoryPath(cat))}
                            />
                        ))}
                    </div>

                    <h3 className="bmn-section__eyebrow">LOCAL SPOTS NEAR YOU</h3>
                    {loading ? (
                        <div className="bmn-loading">
                            <div className="spinner-border spinner-border-sm text-warning" role="status" />
                            <span>&nbsp;Loading...</span>
                        </div>
                    ) : businesses.length === 0 ? (
                        <p className="bmn-empty">No local merchants found.</p>
                    ) : (
                        <div className="bmn-grid">
                            {businesses.slice(0, LOCAL_SPOTS_SHOWN).map(b => (
                                <div key={b.id} className="bmn-card">
                                    <button type="button" className="gcm-card-open" onClick={() => openBusiness(navigate, b)}>
                                        <div className="bmn-card__img">
                                            <MerchantPhoto url={b.image_url} alt={b.name} placeholderClassName="bmn-card__img--placeholder" eager />
                                        </div>
                                        <p className="bmn-card__name">{b.name}</p>
                                    </button>
                                    {b.rating > 0 && <YelpRating rating={b.rating} reviewCount={b.review_count} href={yelpBusinessUrl(b)} />}
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* National Merchants */}
                <section className="bmn-section bmn-section--national">
                    <h2 className="bmn-section__title">National Merchants</h2>
                    <OurMerchants
                        searchPlaceholder={t('search_national_merchants')}
                        groupByCategory
                        collapseToRows
                        toolbarAside={showPfiBanner && (
                            <div className="bmn-banner">
                                <img
                                    src={`${process.env.REACT_APP_API_STORAGE_URL}/theme/default/images/Rewards-Catalog-Banner.jpg`}
                                    alt="Rewards Catalog"
                                    onClick={handleBannerClick}
                                />
                            </div>
                        )}
                    />
                </section>
            </Container>
        </div>
    );
};

export default connect(state => ({
    program: state.program,
}))(BrowseMerchantsNew);
